/**
 * Client-side Nearby POIs lookup (OpenStreetMap).
 *
 * Running this from the user's browser (not from our server):
 *   - Photon (Komoot) for geocoding — no API key, CORS enabled.
 *   - Overpass API for POI radius search — CORS enabled.
 *
 * Why client-side: some public Overpass mirrors rate-limit / ban our server
 * egress IPs, but real end-user IPs are fine.
 *
 * Returns { line, details } where line is a ready-to-paste Italian string
 * like "Metro Cadorna a 180m · Supermercato a 120m · Parco a 350m".
 */

const PHOTON_URL = "https://photon.komoot.io/api/";
const OVERPASS_URL = "https://overpass-api.de/api/interpreter";
const RADIUS_M = 700;

const CATEGORIES = [
  { key: "metro", label: "Metro", q: '[station=subway];[railway=subway_entrance]' },
  { key: "treno", label: "Stazione FS", q: '[railway=station]' },
  { key: "tram", label: "Tram", q: '[railway=tram_stop]' },
  { key: "bus", label: "Bus", q: '[highway=bus_stop]' },
  { key: "supermercato", label: "Supermercato", q: '[shop=supermarket];[shop=convenience]' },
  { key: "farmacia", label: "Farmacia", q: '[amenity=pharmacy]' },
  { key: "scuola_materna", label: "Asilo", q: '[amenity=kindergarten]' },
  { key: "scuola", label: "Scuola", q: '[amenity=school]' },
  { key: "parco", label: "Parco", q: '[leisure=park]' },
  { key: "ospedale", label: "Ospedale", q: '[amenity=hospital]' },
];

function haversineM(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dphi = toRad(lat2 - lat1);
  const dlam = toRad(lng2 - lng1);
  const a =
    Math.sin(dphi / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dlam / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

export async function geocode(address, city) {
  const q = [address, city].filter(Boolean).join(", ");
  if (!q) return null;
  try {
    const r = await fetch(`${PHOTON_URL}?q=${encodeURIComponent(q)}&limit=5`);
    if (!r.ok) return null;
    const data = await r.json();
    const feats = data?.features || [];
    const it = feats.find((f) =>
      ["italia", "italy"].includes((f?.properties?.country || "").toLowerCase())
    ) || feats[0];
    if (!it) return null;
    const [lng, lat] = it.geometry.coordinates;
    return { lat, lng };
  } catch {
    return null;
  }
}

function classify(tags) {
  if (tags.railway === "station" && tags.station === "subway") return "metro";
  if (tags.station === "subway" || tags.railway === "subway_entrance") return "metro";
  if (tags.railway === "station") return "treno";
  if (tags.railway === "tram_stop") return "tram";
  if (tags.highway === "bus_stop") return "bus";
  if (tags.amenity === "kindergarten") return "scuola_materna";
  if (tags.amenity === "school") return "scuola";
  if (tags.shop === "supermarket" || tags.shop === "convenience") return "supermercato";
  if (tags.amenity === "pharmacy") return "farmacia";
  if (tags.leisure === "park" || tags.landuse === "recreation_ground") return "parco";
  if (tags.amenity === "hospital") return "ospedale";
  return null;
}

export async function fetchNearby(lat, lng) {
  const q = `[out:json][timeout:15];(
    node[station=subway](around:${RADIUS_M},${lat},${lng});
    node[railway=subway_entrance](around:${RADIUS_M},${lat},${lng});
    node[railway=station](around:${RADIUS_M},${lat},${lng});
    node[railway=tram_stop](around:${RADIUS_M},${lat},${lng});
    node[highway=bus_stop](around:${RADIUS_M},${lat},${lng});
    node[amenity=kindergarten](around:${RADIUS_M},${lat},${lng});
    node[amenity=school](around:${RADIUS_M},${lat},${lng});
    way[amenity=school](around:${RADIUS_M},${lat},${lng});
    node[shop=supermarket](around:${RADIUS_M},${lat},${lng});
    node[shop=convenience](around:${RADIUS_M},${lat},${lng});
    node[amenity=pharmacy](around:${RADIUS_M},${lat},${lng});
    node[leisure=park](around:${RADIUS_M},${lat},${lng});
    way[leisure=park](around:${RADIUS_M},${lat},${lng});
    node[amenity=hospital](around:${RADIUS_M},${lat},${lng});
  );out tags center 40;`;
  try {
    const r = await fetch(OVERPASS_URL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: "data=" + encodeURIComponent(q),
    });
    if (!r.ok) return {};
    const data = await r.json();
    const els = data.elements || [];
    const best = {};
    for (const el of els) {
      const elat = el.lat ?? el.center?.lat;
      const elng = el.lon ?? el.center?.lon;
      if (elat == null || elng == null) continue;
      const key = classify(el.tags || {});
      if (!key) continue;
      const d = haversineM(lat, lng, elat, elng);
      const prev = best[key];
      if (!prev || d < prev.distance_m) {
        best[key] = {
          label: CATEGORIES.find((c) => c.key === key)?.label || key,
          name: el.tags?.name || el.tags?.brand || null,
          distance_m: d,
        };
      }
    }
    return best;
  } catch {
    return {};
  }
}

export function renderNearbyLine(details) {
  const priority = [
    "metro", "treno", "tram", "bus",
    "supermercato", "farmacia",
    "scuola_materna", "scuola",
    "parco", "ospedale",
  ];
  const chunks = [];
  for (const k of priority) {
    const poi = details[k];
    if (!poi) continue;
    let label = poi.label;
    if (poi.name && ["metro", "treno", "tram"].includes(k)) {
      label = `${label} ${poi.name}`;
    }
    const d = poi.distance_m;
    const dt = d < 1000 ? `${d}m` : `${(d / 1000).toFixed(1)}km`;
    chunks.push(`${label} a ${dt}`);
    if (chunks.length >= 5) break;
  }
  return chunks.join(" · ");
}

export async function nearbySummary(address, city) {
  const geo = await geocode(address, city);
  if (!geo) return { line: "", details: {}, geo: null };
  const details = await fetchNearby(geo.lat, geo.lng);
  return { line: renderNearbyLine(details), details, geo };
}
